import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchReportJson, getGameAnalytics } from "../../services/developerService";
import {
  getDefaultPackageId,
  getPackageDisplayLabel,
  getPackageId,
  getReportPackages,
  isLatestPackage,
  normalizeAnalyticsReportView,
  resolveSelectedArtifact,
} from "../../utils/reportPackageUtils";
import NativeReportView from "./NativeReportView";
import { getReportAssetBaseUrl } from "../../utils/nativeReportUtils";

function toId(value) {
  if (value == null || value === "") return "";
  return String(value);
}

function buildReportSearchParams(gameId, packageId, artifactId) {
  const nextParams = new URLSearchParams();
  if (toId(gameId)) nextParams.set("game", toId(gameId));
  if (toId(packageId)) nextParams.set("package", toId(packageId));
  if (toId(artifactId)) nextParams.set("artifact", toId(artifactId));
  return nextParams;
}

function DeveloperReports({ games, developerId }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAnalyticsGameId, setSelectedAnalyticsGameId] = useState(
    () => searchParams.get("game") || ""
  );
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [selectedArtifactId, setSelectedArtifactId] = useState(
    () => searchParams.get("artifact") || null
  );
  const [selectedPackageId, setSelectedPackageId] = useState(
    () => searchParams.get("package") || null
  );
  const [gameSearchTerm, setGameSearchTerm] = useState("");
  const [packageSearchTerm, setPackageSearchTerm] = useState("");
  const [isReportFullscreen, setIsReportFullscreen] = useState(false);
  const [jsonCache, setJsonCache] = useState({});
  const jsonCacheRef = useRef(jsonCache);
  jsonCacheRef.current = jsonCache;
  const lastQueryRef = useRef(searchParams.toString());

  useEffect(() => {
    if (!games.length) {
      setSelectedAnalyticsGameId((prev) => (prev === "" ? prev : ""));
      return;
    }

    const selectedExists = games.some(
      (game) => toId(game._id || game.id) === toId(selectedAnalyticsGameId)
    );

    if (selectedAnalyticsGameId && selectedExists) {
      return;
    }

    const gameFromUrl = searchParams.get("game") || "";
    const urlExists = games.some((game) => toId(game._id || game.id) === toId(gameFromUrl));
    const nextGameId = urlExists ? gameFromUrl : toId(games[0]?._id || games[0]?.id);

    setSelectedAnalyticsGameId((prev) => (toId(prev) === toId(nextGameId) ? prev : nextGameId));
  }, [games, searchParams, selectedAnalyticsGameId]);

  useEffect(() => {
    const nextParams = buildReportSearchParams(
      selectedAnalyticsGameId,
      selectedPackageId,
      selectedArtifactId
    );
    const nextString = nextParams.toString();
    if (lastQueryRef.current === nextString) {
      return;
    }
    lastQueryRef.current = nextString;
    setSearchParams(nextParams, { replace: true });
  }, [selectedAnalyticsGameId, selectedPackageId, selectedArtifactId, setSearchParams]);

  useEffect(() => {
    if (!isReportFullscreen) return undefined;

    const handleEscClose = (event) => {
      if (event.key === "Escape") {
        setIsReportFullscreen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscClose);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscClose);
    };
  }, [isReportFullscreen]);

  useEffect(() => {
    if (!developerId || !selectedAnalyticsGameId) {
      return;
    }

    const fetchAnalytics = async (packageId = null) => {
      try {
        setAnalyticsLoading(true);
        setAnalyticsError(null);
        setJsonCache({});

        const data = await getGameAnalytics(developerId, selectedAnalyticsGameId, {
          packageId: packageId || undefined,
        });
        setAnalyticsData(data);

        const packages = getReportPackages(data);
        const defaultPackageId = toId(packageId || getDefaultPackageId(packages)) || null;
        setSelectedPackageId((prev) => (toId(prev) === toId(defaultPackageId) ? prev : defaultPackageId));

        const reportView = normalizeAnalyticsReportView(data, defaultPackageId);
        const nextArtifactId = reportView.defaultArtifact?.id || null;
        setSelectedArtifactId((prev) => {
          const stillValid = reportView.viewerTabs.some((tab) => tab.id === toId(prev));
          if (stillValid) return prev;
          return nextArtifactId;
        });
      } catch (err) {
        setAnalyticsData(null);
        setSelectedArtifactId(null);
        setSelectedPackageId(null);
        setAnalyticsError("Failed to load analytics report.");
      } finally {
        setAnalyticsLoading(false);
      }
    };

    fetchAnalytics();
  }, [developerId, selectedAnalyticsGameId]);

  const handlePackageChange = async (packageId) => {
    const nextPackageId = String(packageId);
    setSelectedPackageId(nextPackageId);
    setSelectedArtifactId(null);

    let reportView = normalizeAnalyticsReportView(analyticsData, nextPackageId);

    if (
      reportView.viewerTabs.length === 0 &&
      developerId &&
      selectedAnalyticsGameId
    ) {
      try {
        setAnalyticsLoading(true);
        const data = await getGameAnalytics(developerId, selectedAnalyticsGameId, {
          packageId: nextPackageId,
        });
        setAnalyticsData(data);
        reportView = normalizeAnalyticsReportView(data, nextPackageId);
      } catch (err) {
        setAnalyticsError("Failed to load report package.");
      } finally {
        setAnalyticsLoading(false);
      }
    }

    if (reportView.defaultArtifact?.id) {
      setSelectedArtifactId(reportView.defaultArtifact.id);
    }
  };

  const reportView = normalizeAnalyticsReportView(analyticsData, selectedPackageId);
  const filteredGames = useMemo(() => {
    const normalizedSearch = gameSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return games;

    return games.filter((game) => {
      const gameName = String(game.name || "").toLowerCase();
      const gameId = String(game._id || game.id || "");
      return gameName.includes(normalizedSearch) || gameId === String(selectedAnalyticsGameId);
    });
  }, [games, gameSearchTerm, selectedAnalyticsGameId]);

  const filteredPackages = useMemo(() => {
    const normalizedSearch = packageSearchTerm.trim().toLowerCase();
    if (!normalizedSearch) return reportView.reportPackages;

    return reportView.reportPackages.filter((pkg) => {
      const label = getPackageDisplayLabel(pkg).toLowerCase();
      const pkgId = getPackageId(pkg);
      return label.includes(normalizedSearch) || pkgId === String(reportView.selectedPackageId || "");
    });
  }, [reportView.reportPackages, reportView.selectedPackageId, packageSearchTerm]);

  const selectedArtifact = resolveSelectedArtifact(
    reportView.viewerTabs,
    selectedArtifactId,
    reportView.defaultArtifact
  );
  const jsonCacheKey = selectedArtifact?.jsonArtifactId || selectedArtifact?.safeUrl || "";
  const selectedRenderMode = selectedArtifact?.renderMode;
  const selectedJsonUrl = selectedArtifact?.safeUrl;
  const selectedJsonArtifactId = selectedArtifact?.jsonArtifactId;

  useEffect(() => {
    if (selectedRenderMode !== "json" || !jsonCacheKey) {
      return undefined;
    }

    if (jsonCacheRef.current[jsonCacheKey]?.status === "loaded") {
      return undefined;
    }

    let cancelled = false;

    fetchReportJson(
      { safeUrl: selectedJsonUrl, jsonArtifactId: selectedJsonArtifactId },
      { developerId, gameId: selectedAnalyticsGameId }
    )
      .then((data) => {
        if (!cancelled) {
          setJsonCache((prev) => ({ ...prev, [jsonCacheKey]: { status: "loaded", data } }));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setJsonCache((prev) => ({
            ...prev,
            [jsonCacheKey]: {
              status: "error",
              error: error?.message || "Failed to load report JSON.",
            },
          }));
        }
      });

    setJsonCache((prev) => {
      if (prev[jsonCacheKey]?.status === "loaded") return prev;
      return { ...prev, [jsonCacheKey]: { status: "loading" } };
    });

    return () => {
      cancelled = true;
    };
  }, [
    jsonCacheKey,
    selectedRenderMode,
    selectedJsonUrl,
    selectedJsonArtifactId,
    developerId,
    selectedAnalyticsGameId,
  ]);

  const jsonState = jsonCache[jsonCacheKey] || null;
  const nativeJson = selectedArtifact?.renderMode === "json" ? jsonState?.data : null;
  const useNativeJson = selectedArtifact?.renderMode === "json" && Boolean(nativeJson);
  const jsonError = selectedArtifact?.renderMode === "json" && jsonState?.status === "error"
    ? jsonState.error
    : null;
  const waitingForJson =
    selectedArtifact?.renderMode === "json" && !useNativeJson && !jsonError;
  const activeReportUrl = reportView.useLegacySingleReport
    ? reportView.legacyFeedbackReportUrl
    : selectedArtifact?.htmlFallbackUrl || selectedArtifact?.safeUrl || null;
  const activeReportGeneratedAt = reportView.useLegacySingleReport
    ? reportView.legacyFeedbackReportGeneratedAt
    : null;
  const selectedGame = games.find(
    (game) => String(game._id || game.id) === String(selectedAnalyticsGameId)
  );

  return (
    <div className="dev-card">
      <div className="dev-card-header">
        <h2 className="dev-card-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22,12 18,12 15,21 9,3 6,12 2,12" />
          </svg>
          Game Reports
        </h2>
      </div>
      <div className="dev-card-body">
        {games.length === 0 ? (
          <p className="dev-empty">Create a game to view report packages.</p>
        ) : (
          <>
            <div className="report-package-controls">
              <div className="report-package-control">
                <label htmlFor="analytics-game-select" className="report-package-control-label">
                  Select game
                </label>
                <input
                  type="text"
                  className="report-package-search-input"
                  placeholder="Search games..."
                  value={gameSearchTerm}
                  onChange={(e) => setGameSearchTerm(e.target.value)}
                />
                <select
                  id="analytics-game-select"
                  className="report-package-select"
                  value={selectedAnalyticsGameId}
                  onChange={(e) => setSelectedAnalyticsGameId(e.target.value)}
                >
                  {filteredGames.map((game) => (
                    <option key={String(game._id || game.id)} value={String(game._id || game.id)}>
                      {game.name || `Game ${game._id || game.id}`}
                    </option>
                  ))}
                </select>
                {filteredGames.length === 0 && (
                  <p className="report-package-inline-message">No games match your search.</p>
                )}
              </div>

              {reportView.reportPackages.length > 1 && (
                <div className="report-package-control">
                  <label htmlFor="analytics-package-select" className="report-package-control-label">
                    Report package
                  </label>
                  <input
                    type="text"
                    className="report-package-search-input"
                    placeholder="Search packages..."
                    value={packageSearchTerm}
                    onChange={(e) => setPackageSearchTerm(e.target.value)}
                  />
                  <select
                    id="analytics-package-select"
                    className="report-package-select"
                    value={reportView.selectedPackageId || ""}
                    onChange={(e) => handlePackageChange(e.target.value)}
                    disabled={analyticsLoading}
                  >
                    {filteredPackages.map((pkg) => (
                      <option key={getPackageId(pkg)} value={getPackageId(pkg)}>
                        {getPackageDisplayLabel(pkg)}
                      </option>
                    ))}
                  </select>
                  {filteredPackages.length === 0 && (
                    <p className="report-package-inline-message">No packages match your search.</p>
                  )}
                </div>
              )}
            </div>

            {analyticsLoading && (
              <p className="report-package-message">Loading report package...</p>
            )}

            {analyticsError && (
              <p className="report-package-message report-package-message--error">
                {analyticsError}
              </p>
            )}

            {!analyticsLoading && !analyticsError && reportView.emptyState === "no_package" && (
              <p className="report-package-message">
                No ready report package available for this game.
              </p>
            )}

            {!analyticsLoading && !analyticsError && (reportView.emptyState === "no_html_artifacts" || reportView.emptyState === "no_artifacts") && (
              <p className="report-package-message">
                Report package found, but no report pages are available yet.
                {reportView.reportPackage?.name ? ` (${reportView.reportPackage.name})` : ""}
              </p>
            )}

            {!analyticsLoading && !analyticsError && reportView.emptyState === "no_renderable_url" && (
              <p className="report-package-message">
                Report pages are listed, but no viewable URLs are available yet.
              </p>
            )}

            {!analyticsLoading && !analyticsError && reportView.hasRenderableReport && (
              <div className={`report-package-viewer ${isReportFullscreen ? "report-package-viewer--fullscreen" : ""}`}>
                <div className="report-package-toolbar">
                  <div className="report-package-meta">
                    <span className="report-package-meta-game">
                      {selectedGame?.name || "Selected game"}
                    </span>
                    {reportView.reportPackage?.name && (
                      <span className="report-package-meta-detail">
                        {reportView.reportPackage.name}
                      </span>
                    )}
                    {reportView.reportPackage?.status && (
                      <span
                        className={`report-package-status report-package-status--${String(reportView.reportPackage.status).toLowerCase()}`}
                      >
                        {reportView.reportPackage.status}
                      </span>
                    )}
                    {reportView.reportPackage && isLatestPackage(reportView.reportPackage) && (
                      <span className="report-package-latest-badge">Latest</span>
                    )}
                    {activeReportGeneratedAt && (
                      <span className="report-package-meta-detail">
                        Generated {new Date(activeReportGeneratedAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  {activeReportUrl && (
                    <div className="report-package-toolbar-actions">
                      <button
                        type="button"
                        className="dev-btn dev-btn--ghost dev-btn--sm"
                        onClick={() => setIsReportFullscreen((prev) => !prev)}
                      >
                        {isReportFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                      </button>
                      <a
                        href={activeReportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dev-btn dev-btn--ghost dev-btn--sm"
                      >
                        Open in new tab
                      </a>
                    </div>
                  )}
                </div>

                {!reportView.useLegacySingleReport && reportView.viewerTabs.length > 0 && (
                  <div className="report-artifact-tabs" role="tablist" aria-label="Report sections">
                    {reportView.viewerTabs.map((artifact) => (
                      <button
                        key={artifact.id}
                        type="button"
                        role="tab"
                        aria-selected={selectedArtifact?.id === artifact.id}
                        className={`report-artifact-tab ${selectedArtifact?.id === artifact.id ? "active" : ""}`}
                        onClick={() => setSelectedArtifactId(artifact.id)}
                      >
                        {artifact.displayTitle}
                      </button>
                    ))}
                  </div>
                )}

                {useNativeJson ? (
                  <div className="native-report-frame">
                    <NativeReportView
                      data={nativeJson}
                      viewKind={selectedArtifact.viewKind}
                      assetBaseUrl={getReportAssetBaseUrl(selectedArtifact.safeUrl || selectedArtifact.htmlFallbackUrl)}
                    />
                  </div>
                ) : waitingForJson ? (
                  <p className="report-package-message">Loading report...</p>
                ) : jsonError && !selectedArtifact?.htmlFallbackUrl ? (
                  <p className="report-package-message report-package-message--error">
                    {jsonError}
                  </p>
                ) : activeReportUrl ? (
                  <>
                    {jsonError ? (
                      <p className="report-package-inline-message">
                        Could not render JSON in the dashboard, showing the HTML report instead.
                      </p>
                    ) : null}
                    <iframe
                      title={selectedArtifact?.displayTitle || "Game analytics report"}
                      src={activeReportUrl}
                      className="report-package-iframe"
                    />
                  </>
                ) : (
                  <p className="report-package-message">
                    Selected report page does not have a viewable URL yet.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DeveloperReports;
